import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FiArrowLeft } from "react-icons/fi";
import { styled } from "styled-components";

import Screens from "../screens";
import Projects from "../screens/projects/Projects";
import ProjectDetails from "../screens/projects/pages/ProjectDetails";
import ProjectsWrapper from "../screens/projects/pages";
import CuriousDetails from "../screens/curious/CuriousDetails";
import HomeHeading from "./HomeHeading";
import PageTransition from "./PageTransition";

function getRouteKey(location) {
  return `${location.pathname}${location.search}`;
}

function scrollToPageTop() {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "instant",
  });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

function scrollToHash(hash) {
  if (!hash) {
    return;
  }

  const target = document.getElementById(decodeURIComponent(hash.slice(1)));

  if (!target) {
    return;
  }

  target.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

export default function AppRoutes() {
  const location = useLocation();
  const pendingLocation = useRef(null);
  const pendingHash = useRef("");
  const activeRouteKey = useRef(getRouteKey(location));

  const [renderedLocation, setRenderedLocation] = useState(location);
  const [transitionKey, setTransitionKey] = useState(null);

  const browserRouteKey = getRouteKey(location);
  const renderedRouteKey = getRouteKey(renderedLocation);

  useEffect(() => {
    if (!("scrollRestoration" in window.history)) {
      return undefined;
    }

    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useEffect(() => {
    if (browserRouteKey === activeRouteKey.current) {
      return;
    }

    activeRouteKey.current = browserRouteKey;
    pendingLocation.current = location;

    window.dispatchEvent(new Event("page-transition:start"));
    setTransitionKey(`${browserRouteKey}-${Date.now()}`);
  }, [browserRouteKey, location]);

  const revealPendingRoute = useCallback(() => {
    if (!pendingLocation.current) {
      return;
    }

    const nextLocation = pendingLocation.current;
    pendingHash.current = nextLocation.hash;

    window.dispatchEvent(
      new CustomEvent("page-transition:update-scroll-lock", {
        detail: { top: 0 },
      }),
    );

    scrollToPageTop();
    setRenderedLocation(nextLocation);
    pendingLocation.current = null;
  }, []);

  useEffect(() => {
    const scrollAfterTransition = () => {
      const hash = pendingHash.current;
      pendingHash.current = "";

      window.setTimeout(() => {
        scrollToHash(hash);
      }, 0);
    };

    window.addEventListener("page-transition:complete", scrollAfterTransition);

    return () => {
      window.removeEventListener(
        "page-transition:complete",
        scrollAfterTransition,
      );
    };
  }, []);

  return (
    <>
      <AnimatePresence mode="wait">
        <motion.div
          key={renderedRouteKey}
          initial={{ opacity: 0, y: 34 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -22 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: "100%" }}
        >
          <Routes location={renderedLocation}>
            <Route path="/" element={<Screens />} />
            <Route path="/curious/:slug" element={<CuriousDetails />} />
            <Route path="/projects" element={<ProjectsWrapper />}>
              <Route
                path="/projects"
                element={
                  <>
                    <div className="block-space" />
                    <AllProjectsNav>
                      <Link
                        className="page-nav-button"
                        to="/"
                        aria-label="Back to home"
                      >
                        <FiArrowLeft />
                      </Link>
                    </AllProjectsNav>
                    <HomeHeading>Go-Getter Projects</HomeHeading>
                    <Projects showIntro />
                  </>
                }
              />
              <Route path="/projects/:project" element={<ProjectDetails />} />
            </Route>
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </motion.div>
      </AnimatePresence>

      {transitionKey && (
        <PageTransition
          key={`route-transition-${transitionKey}`}
          variant="route"
          onCovered={revealPendingRoute}
        />
      )}
    </>
  );
}

const AllProjectsNav = styled.div`
  width: 100%;
  max-width: 1120px;
  margin: 0 auto 48px;

  .page-nav-button {
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;

    border: 1px solid #3d3d3d;
    border-radius: 50%;
    background: rgba(14, 14, 14, 0.72);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);

    color: #ababab;
    text-decoration: none;

    transition:
      border-color 0.2s ease,
      color 0.2s ease,
      transform 0.2s ease;

    &:hover {
      border-color: #fff;
      color: #fff;
      transform: translateX(-3px);
    }
  }

  @media (max-width: 760px) {
    margin-bottom: 34px;
  }
`;
